import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "./ProductDetail.css";
import image from "../assets/mat.png";
import OverallScoreTooltip from "../components/OverallScoreTooltip";

function ProductDetail() {
  const { productId, purpose } = useParams();

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [overallScoresWeights, setOverallScoresWeights] = useState({});
  const [showTooltip, setShowTooltip] = useState(false);

  const getColor = (value) =>
    value >= 8 ? "#4CAF50" : value >= 5 ? "#FFC107" : "#F44336";

  /* =========================================================
     📡 1️⃣ Busca produto
  ========================================================= */
  useEffect(() => {
    setLoading(true);
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/v1/products/${productId}/${purpose}`
      )
      .then((res) => setProduct(res.data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [productId, purpose]);

  /* =========================================================
     ⚙️ 2️⃣ Busca configuração de pesos
  ========================================================= */
  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/v1/interface-configuration-detail`
      )
      .then((res) => {
        setOverallScoresWeights(res.data?.overallScoresWeights || {});
      })
      .catch((err) => console.error("Erro carregando config detail:", err));
  }, []);

  /* =========================================================
     🔒 3️⃣ Fecha tooltip ao clicar fora
  ========================================================= */
  useEffect(() => {
    const handleClickOutside = () => setShowTooltip(false);
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* =========================================================
     🧩 4️⃣ Determina o label do propósito e seus pesos
  ========================================================= */
  const purposeLabelMap = {
    generic: "Uso geral",
    gamer: "Gamer",
    editing: "Edição de imagem e video",
  };
  const matchedPurposeLabel =
    purposeLabelMap[purpose] ||
    Object.keys(overallScoresWeights || {})[0] ||
    null;

  const weightsForPurpose = overallScoresWeights[matchedPurposeLabel] || {};

  if (loading) {
    return (
      <div className="detail-loading-indicator-dark">
        <div className="detail-spinner"></div>
        <p>Carregando detalhes...</p>
      </div>
    );
  }

  if (!product) {
    return <p className="detail-no-results-dark">Produto não encontrado</p>;
  }

  const formatCurrency = (value) =>
    typeof value === "number"
      ? value.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 0,
        })
      : "— ";

  return (
    <div className="detail-main-container-dark">
      <div className="detail-product-detail-layout-dark">
        {/* Coluna esquerda: análise + especificações + outros */}
        <div className="detail-analysis-column">
          {/* Análise MAT */}
          <div className="detail-analysis-container">
            <div className="detail-speech-bubble-container">
              <div className="detail-mat-image-container">
                <img
                  src={image}
                  alt="MAT - Especialista em Tecnologia"
                  className="detail-mat-image"
                />
              </div>
              <div className="detail-speech-bubble">
                <div className="detail-speech-bubble-header">
                  <h3>Análise do MAT</h3>
                  <div className="detail-speech-bubble-arrow"></div>
                </div>
                <div className="detail-speech-bubble-content">
                  <h5>{product.analysis}</h5>
                </div>
              </div>
            </div>
          </div>

          {/* Especificações técnicas */}
          <div className="detail-specs-container">
            <div className="detail-specs-card-dark">
              <div className="detail-specs-header-container-dark">
                <h4 className="detail-specs-title-dark">
                  Especificações Técnicas
                </h4>
              </div>
              <div className="detail-specs-grid-dark">
                {product.technicalSpecifications
                  ?.slice() // cria uma cópia para não mutar o original
                  .sort((a, b) => a.label.localeCompare(b.label, "pt-BR")) // ordena alfabeticamente pelo label
                  .map((spec, idx) => (
                    <div key={idx} className="detail-spec-section-dark">
                      <div className="detail-spec-section-header-dark">
                        <div className="detail-spec-section-title-wrapper">
                          <div className="detail-spec-section-title-dark">
                            {spec.label}
                          </div>
                        </div>

                        {spec.score != null && (
                          <div className="detail-spec-section-score-wrapper-dark">
                            <span className="detail-spec-section-score-dark">
                              <span
                                className="detail-score-value"
                                style={{ color: getColor(spec.score) }}
                              >
                                {spec.score.toFixed(1)}
                              </span>

                              <span
                                className="detail-info-icon-inside"
                                data-tooltip="Nota atribuida conforme o propósito selecionado."
                              >
                                ⓘ
                              </span>
                            </span>
                          </div>
                        )}
                      </div>

                      <table className="detail-specs-inner-table-dark">
                        <tbody>
                          {Object.entries(spec)
                            .filter(([key, value]) => {
                              if (key === "label" || value === null)
                                return false;
                              if (typeof value === "object") {
                                return (
                                  value?.value !== null && value?.value !== ""
                                );
                              }
                              return false;
                            })
                            .map(([key, field], i) => (
                              <tr key={`${idx}-${i}`}>
                                <td className="detail-spec-label-dark">
                                  {field.label}
                                </td>
                                <td className="detail-spec-value-dark">
                                  {field.value}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Coluna direita: card fixo do notebook */}
        <div className="detail-product-column">
          <div className="detail-notebook-card-dark">
            <div className="detail-notebook-image-wrapper-dark">
              <img
                src={product.pictures?.[0]}
                alt={product.title}
                className="detail-notebook-image-dark"
              />

              <div className="detail-overall-score-container-dark">
                <span className="detail-star-icon">★</span>
                <span
                  className="detail-overall-score-value tooltip-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTooltip((prev) => !prev);
                  }}
                >
                  {product.overallScore != null
                    ? `${product.overallScore.toFixed(1)}/10`
                    : "N/A"}
                </span>

                <OverallScoreTooltip
                  visible={showTooltip}
                  purposeLabel={matchedPurposeLabel}
                  weights={weightsForPurpose}
                  onClose={() => setShowTooltip(false)}
                />
              </div>
            </div>

            <div className="detail-notebook-content-dark">
              <h3 className="detail-notebook-title-dark">{product.title}</h3>

              <div className="detail-notebook-price-dark">
                <span className="price-value">
                  {formatCurrency(product?.price)}
                </span>
                <span
                  className="price-info-icon"
                  data-tooltip="Preços mudam com frequência"
                >
                  ⓘ
                </span>
              </div>

              <a
                href={product.affiliatedLink || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="detail-cta-button-dark"
              >
                Ver oferta
              </a>
            </div>
            {product.marketplaceName && (
              <div className="detail-marketplace-info-dark">
                Oferta em {product.marketplaceName}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
